import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IntelligenceTypes } from '@mono/proto-clients';
import { SpecraButton, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, ExternalLink, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deliveryClient } from '../../../lib/connect/clients';
import { isRunSettled, useIntelligenceRunProgress } from '../../../pages/Delivery/_shared/hooks/intelligenceHooks';
import { useTenantId } from '../../../pages/Delivery/_shared/hooks/useTenantId';
import { PricingSheetIngestProgress } from '../../../pages/Delivery/Registries/PricingSheetIngestProgress';
import { useChatLinks } from '../ChatLinksContext';
const State = IntelligenceTypes.IntelligenceRunState;
function param(row, key) {
    const v = row.params?.[key];
    return typeof v === 'string' ? v.trim() : '';
}
/** What the card calls a sheet: the agent's title, else its file name. */ /**
 * The source link, only when it is a web address. source_url comes from the
 * agent's action params, which a page it read can influence: a javascript: or
 * data: value must never become a clickable link.
 */ export function webUrl(value) {
    if (!value) return undefined;
    try {
        const url = new URL(value.trim());
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
    } catch  {
        return undefined;
    }
}
export function stageSheetTitle(row) {
    return param(row, 'sheet_title') || row.fileNames || 'Rate sheet';
}
/** A card is a staging card when every row stages a pricing sheet. */ export function isPricingStage(actions) {
    return actions.length > 0 && actions.every((a)=>a.action === 'extract_pricing_sheet');
}
/** The file name a source URL ends in, for the link text. */ export function sourceName(url) {
    try {
        const last = new URL(url).pathname.split('/').filter(Boolean).pop();
        return last ? decodeURIComponent(last) : url;
    } catch  {
        return url;
    }
}
/**
 * Opens an app page with the chat kept beside it: from a full-page chat the
 * chat moves to the side rail; in the rail the page just changes.
 */ function OpenBesideButton({ href, children }) {
    const chat = useChatLinks();
    const navigate = useNavigate();
    return /*#__PURE__*/ _jsx(SpecraButton, {
        type: "button",
        size: "sm",
        variant: "secondary",
        "data-testid": Testids.assistant.pricingStageOpenReview,
        iconRight: /*#__PURE__*/ _jsx(ArrowRight, {
            className: "h-3.5 w-3.5"
        }),
        onClick: ()=>chat?.openInApp ? chat.openInApp(href) : navigate(href),
        children: children
    });
}
/** Where a settled run's review lives: found by the file it was staged from. */ function ReadyForReview({ fileId, step }) {
    const tenantId = useTenantId();
    const { data: review } = useQuery({
        queryKey: [
            'pricing stage review',
            tenantId,
            fileId
        ],
        enabled: Boolean(tenantId && fileId),
        staleTime: 15000,
        queryFn: async ()=>{
            const resp = await deliveryClient.listPricingSheetReviews({
                companyId: tenantId,
                contractVehicleId: '',
                includeApplied: true
            });
            return resp.reviews.find((r)=>r.sourceDocumentId === fileId) ?? null;
        }
    });
    const committed = review?.state === 'Applied';
    const detail = review ? [
        `${review.rates.toLocaleString()} rates`,
        review.categories ? `${review.categories.toLocaleString()} new labor categories` : ''
    ].filter(Boolean).join(' · ') : step;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx(CheckCircle2, {
                className: "h-4 w-4 text-success",
                "aria-hidden": "true"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "success",
                children: committed ? 'Committed to the rate book' : 'Ready for review'
            }),
            detail ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: detail
            }) : null,
            /*#__PURE__*/ _jsx("span", {
                className: "ml-auto",
                children: /*#__PURE__*/ _jsx(OpenBesideButton, {
                    href: review ? `/registries/pricing-sheets/${review.changeSetId}` : '/registries?tab=pricing-sheets',
                    children: committed ? 'View in registry' : 'Open review'
                })
            })
        ]
    });
}
/** After Accept: the run's live steps, then where its review is. */ function StageRunStatus({ runId, fileId, title }) {
    const { data: run } = useIntelligenceRunProgress(runId);
    if (!isRunSettled(run?.state)) {
        return /*#__PURE__*/ _jsx(PricingSheetIngestProgress, {
            runId: runId,
            filename: title
        });
    }
    if (run?.state === State.FAILED || run?.state === State.CANCELLED) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            size: "meta",
            tone: "danger",
            children: run.currentStep || 'This sheet could not be read.'
        });
    }
    return /*#__PURE__*/ _jsx(ReadyForReview, {
        fileId: fileId,
        step: run?.currentStep ?? ''
    });
}
/**
 * The body of a staging card: what Accept does, then each sheet — whose it is,
 * what is in it, where it came from — and, once accepted, its progress and a
 * link to its review that keeps the chat beside the page.
 */ export function PricingSheetStageList({ rows, accepted }) {
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.assistant.pricingStageList,
        children: [
            !accepted ? /*#__PURE__*/ _jsx("div", {
                className: "border-b border-border px-3 py-2",
                children: /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: [
                        "Accept reads ",
                        rows.length === 1 ? 'this sheet' : `these ${rows.length} sheets`,
                        " into a review under Registries → Pricing sheets. Your rate book does not change until the review is committed there."
                    ]
                })
            }) : null,
            /*#__PURE__*/ _jsx("ul", {
                className: "divide-y divide-border",
                children: rows.map((row)=>{
                    const title = stageSheetTitle(row);
                    const summary = param(row, 'sheet_summary');
                    const rawSource = param(row, 'source_url');
                    const source = webUrl(rawSource);
                    const runId = row.result?.apply?.runId ?? '';
                    const fileId = row.result?.apply?.id ?? '';
                    return /*#__PURE__*/ _jsxs("li", {
                        className: "flex flex-col gap-1.5 px-3 py-2.5",
                        "data-testid": Testids.assistant.pricingStageSheet,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-start gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(FileSpreadsheet, {
                                        className: "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground",
                                        "aria-hidden": "true"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-0 flex-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                size: "body-sm",
                                                className: "block font-medium",
                                                children: title
                                            }),
                                            summary ? /*#__PURE__*/ _jsx(SpecraText, {
                                                size: "meta",
                                                tone: "muted",
                                                className: "block",
                                                children: summary
                                            }) : null,
                                            source ? /*#__PURE__*/ _jsxs("a", {
                                                href: source,
                                                target: "_blank",
                                                rel: "noopener noreferrer",
                                                className: "inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:underline",
                                                children: [
                                                    sourceName(source) === title ? 'Source' : sourceName(source),
                                                    /*#__PURE__*/ _jsx(ExternalLink, {
                                                        className: "h-3 w-3",
                                                        "aria-hidden": "true"
                                                    })
                                                ]
                                            }) : rawSource ? /*#__PURE__*/ _jsx(SpecraText, {
                                                size: "meta",
                                                tone: "muted",
                                                className: "block break-all",
                                                children: rawSource
                                            }) : row.fileNames && row.fileNames !== title ? /*#__PURE__*/ _jsx(SpecraText, {
                                                size: "meta",
                                                tone: "muted",
                                                className: "block",
                                                children: row.fileNames
                                            }) : null
                                        ]
                                    })
                                ]
                            }),
                            row.result?.status === 'fail' ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "danger",
                                children: `Could not start: ${row.result.message || 'try again'}`
                            }) : row.result?.status === 'skipped' ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: row.result.message || 'Skipped'
                            }) : runId ? /*#__PURE__*/ _jsx(StageRunStatus, {
                                runId: runId,
                                fileId: fileId,
                                title: title
                            }) : null
                        ]
                    }, row.id);
                })
            })
        ]
    });
}
